import { TRAP_KEYS } from "@ca/shared";
import { z } from "zod";

/**
 * Mô hình nội dung chuẩn. Script nhập liệu chuyển dữ liệu demo về dạng này; bộ nạp ghi dạng này vào DB.
 * Chuỗi có hậu tố `Html` đã qua `sanitizeContentHtml`.
 */

export const OptionKey = z.enum(["A", "B", "C", "D"]);
export type OptionKey = z.infer<typeof OptionKey>;

export const QuestionOption = z.strictObject({
  key: OptionKey,
  /** Rỗng ở Part 1–2: người học chỉ nghe, không thấy chữ. */
  text: z.string(),
});

/** Phân tích một lựa chọn trong lời giải. */
export const ExplanationOption = z.strictObject({
  key: OptionKey,
  /** Nghĩa hoặc chú giải ngắn của lựa chọn. */
  gloss: z.string().optional(),
  /** Vì sao đúng hoặc sai. */
  verdictHtml: z.string().optional(),
  /** Từ khoá của lựa chọn (Part 7). */
  keyword: z.string().optional(),
  /** Lời đọc của lựa chọn và bản dịch (Part 1–2, nơi lựa chọn chỉ có tiếng). */
  spokenEn: z.string().optional(),
  spokenVi: z.string().optional(),
});

export const Explanation = z.strictObject({
  schemaVersion: z.literal(1),
  /** Nhãn dạng câu gốc trong đề, hiển thị ở chip lời giải. Nhãn chuẩn hoá nằm ở `skill`. */
  category: z.string(),
  tag: z.string().optional(),
  kindLabel: z.string().optional(),
  difficultyLabel: z.string().optional(),
  whyHtml: z.string().optional(),
  tipsHtml: z.array(z.string()).optional(),
  extraHtml: z.string().optional(),
  paraphraseHtml: z.string().optional(),
  trap: z.strictObject({ name: z.string(), html: z.string() }).optional(),
  options: z.array(ExplanationOption),
  questionKeywords: z.array(z.string()).optional(),
  evidence: z
    .strictObject({
      quotes: z.array(z.string()),
      citeHtml: z.string().optional(),
      citeVi: z.string().optional(),
    })
    .optional(),
  /** Phân tích cấu trúc câu (Part 5). */
  structure: z
    .strictObject({
      segments: z.array(z.strictObject({ text: z.string(), label: z.string(), kind: z.string() })),
      summary: z.string().optional(),
      noteHtml: z.string().optional(),
      needHtml: z.string().optional(),
    })
    .optional(),
  /** Cách ghép thông tin giữa các văn bản (câu nối văn bản, Part 7). */
  cross: z
    .strictObject({
      tipHtml: z.string().optional(),
      sources: z.array(
        z.strictObject({ label: z.string(), quoteHtml: z.string(), vi: z.string() }),
      ),
      combineHtml: z.string().optional(),
    })
    .optional(),
  /** Câu hỏi được đọc lên (Part 2), nơi đề không in chữ. */
  spoken: z.strictObject({ en: z.string().min(1), vi: z.string().optional() }).optional(),
});
export type Explanation = z.infer<typeof Explanation>;

export const Translation = z.strictObject({
  question: z.string().optional(),
  options: z.array(z.string()).optional(),
});
export type Translation = z.infer<typeof Translation>;

export const VocabNote = z.strictObject({ term: z.string(), meaningVi: z.string() });

export const SkillRef = z.strictObject({ part: z.number().int().min(1).max(7), name: z.string() });

export const CanonicalQuestion = z.strictObject({
  number: z.number().int().positive(),
  part: z.number().int().min(1).max(7),
  /** Mã cụm; null ở Part 5. */
  pid: z.string().nullable(),
  prompt: z.string(),
  options: z.array(QuestionOption).min(3).max(4),
  correct: OptionKey,
  skill: SkillRef,
  /** Chỉ gán cho Part 7, nơi thống kê bẫy có nghĩa. */
  trapKey: z.enum(TRAP_KEYS).nullable(),
  isCrosstext: z.boolean(),
  difficulty: z.number().int().min(1).max(5).nullable(),
  level: z.number().int().min(1).max(5).nullable(),
  poolKey: z.string().nullable(),
  explanation: Explanation,
  translation: Translation,
  vocab: z.array(VocabNote),
});
export type CanonicalQuestion = z.infer<typeof CanonicalQuestion>;

export const TranscriptLine = z.strictObject({
  speaker: z.string(),
  /** Có thể chứa đánh dấu dẫn chứng `span.ev`. */
  enHtml: z.string(),
  vi: z.string(),
});
export type TranscriptLine = z.infer<typeof TranscriptLine>;

export const CanonicalPassage = z.strictObject({
  pid: z.string(),
  part: z.number().int().min(1).max(7),
  sort: z.number().int().nonnegative(),
  /** Bản đầy đủ cho màn xem lại: có đánh dấu dẫn chứng. Null khi cụm chỉ có tiếng và ảnh. */
  html: z.string().nullable(),
  /** Bản đưa cho người học trước khi chấm: không đánh dấu dẫn chứng, không lời thoại. */
  publicHtml: z.string().nullable(),
  translationVi: z.string().nullable(),
  docCount: z.number().int().min(1).max(3),
  /** Đường dẫn file trong bản demo; bộ nạp đổi sang khoá lưu trữ theo bảng kê media. */
  audio: z.string().nullable(),
  images: z.array(z.string()),
  transcript: z.array(TranscriptLine).nullable(),
  isFree: z.boolean(),
});
export type CanonicalPassage = z.infer<typeof CanonicalPassage>;

export const DictationLine = z.strictObject({
  /** Câu (Part 1–2) hoặc cụm (Part 3–4) mà dòng này thuộc về. */
  owner: z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("question"), number: z.number().int() }),
    z.strictObject({ type: z.literal("passage"), pid: z.string() }),
  ]),
  part: z.number().int().min(1).max(4),
  lineNo: z.number().int().nonnegative(),
  speaker: z.string().nullable(),
  textEn: z.string().min(1),
  textVi: z.string().nullable(),
  clip: z.string().nullable(),
});
export type DictationLine = z.infer<typeof DictationLine>;

export const CanonicalTest = z.strictObject({
  code: z.string(),
  setCode: z.string(),
  name: z.string(),
  kind: z.enum(["ets", "mock", "rc"]),
  no: z.number().int().positive(),
  parts: z.array(z.number().int().min(1).max(7)),
  durationMin: z.number().int().positive(),
  status: z.enum(["draft", "published"]),
  passages: z.array(CanonicalPassage),
  questions: z.array(CanonicalQuestion),
  dictation: z.array(DictationLine),
});
export type CanonicalTest = z.infer<typeof CanonicalTest>;

export const CanonicalRemedyItem = z.strictObject({
  kind: z.enum(["skill", "trap"]),
  key: z.string(),
  part: z.number().int().min(5).max(7),
  sort: z.number().int().nonnegative(),
  passageHtml: z.string().nullable(),
  prompt: z.string(),
  options: z.array(QuestionOption).length(4),
  correct: OptionKey,
  explanation: Explanation,
  translation: Translation,
  isFree: z.boolean(),
});
export type CanonicalRemedyItem = z.infer<typeof CanonicalRemedyItem>;

export const CanonicalLesson = z.strictObject({
  key: z.string(),
  title: z.string(),
  groupKey: z.enum(["p5", "p6", "p7", "trap"]),
  html: z.string(),
  sort: z.number().int().nonnegative(),
});

export const CanonicalVocabTopic = z.strictObject({
  slug: z.string(),
  name: z.string(),
  nameEn: z.string(),
  icon: z.string(),
  sort: z.number().int().nonnegative(),
  isFree: z.boolean(),
});

export const CanonicalVocabWord = z.strictObject({
  word: z.string().min(1),
  pos: z.string(),
  ipa: z.string().nullable(),
  meaningVi: z.string().min(1),
  cefr: z.string().nullable(),
  topic: z.string(),
  example: z.string().nullable(),
});
